import { CALL_BOOST_GAIN_MAX, CALL_BOOST_GAIN_MIN } from '../lib/callAudioBoostPrefs'
import { useCallBoost } from '../lib/useCallBoost'

// Inline (non-popover) version of the boost control. Used outside an
// active call — sidebar on desktop, settings modal on mobile — so the
// operator can configure the preference without waiting for a call to
// arrive. Same switch + slider + help text as CallBoostControl, just
// rendered in place rather than inside a portal'd popover.

interface Props {
  /** Optional id used by tests to find the slider/switch. */
  controlsTestId?: string
}

function formatPercent(g: number): string {
  return `${Math.round(g * 100)}%`
}

export function CallBoostPanel({ controlsTestId }: Props): React.ReactElement {
  const { enabled, gain, setEnabled, setGain } = useCallBoost()

  const sliderTestId = controlsTestId ? `${controlsTestId}-slider` : undefined
  const switchTestId = controlsTestId ? `${controlsTestId}-switch` : undefined

  return (
    <div className="px-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Усиление
        </span>
        <label className="inline-flex items-center cursor-pointer select-none">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid={switchTestId}
            aria-label="Включить усиление звука звонка"
          />
          <span className="w-9 h-5 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 transition-colors relative after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:shadow-sm after:transition-transform peer-checked:after:translate-x-4" />
        </label>
      </div>

      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          Громкость
        </span>
        <span className={`text-xs font-mono tabular-nums ${
          enabled && gain > 1 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-500 dark:text-gray-400'
        }`}>
          {formatPercent(gain)}
        </span>
      </div>
      <input
        type="range"
        min={CALL_BOOST_GAIN_MIN * 100}
        max={CALL_BOOST_GAIN_MAX * 100}
        step={5}
        value={Math.round(gain * 100)}
        disabled={!enabled}
        onChange={(e) => setGain(Number(e.target.value) / 100)}
        data-testid={sliderTestId}
        className="w-full accent-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Уровень усиления входящего звука"
      />
      <div className="flex justify-between text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">
        <span>100</span>
        <span>200</span>
        <span>300</span>
      </div>

      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 leading-snug">
        Программное усиление входящего звука. Включи если плохо слышно собеседника.
        На 200%+ возможны лёгкие искажения на пиках.
      </p>
    </div>
  )
}
